import { useEffect, useState, useRef } from "react";
import { motion } from "framer-motion";
import { 
  Globe, Link2, FileText, Image, Search, Eye, EyeOff, 
  Save, ChevronDown, ChevronUp, ExternalLink, Copy, Check,
  Upload, Loader2
} from "lucide-react";
import { Button } from "../ui/button";
import axios from "axios";
import { API, getAuthHeader } from "./utils";

export const PageSEOTab = () => {
  const [pages, setPages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState({});
  const [expandedPage, setExpandedPage] = useState(null);
  const [copied, setCopied] = useState(null);
  const [uploading, setUploading] = useState({});
  const [imageInputMode, setImageInputMode] = useState({}); // 'url' or 'upload'
  const [saveSuccess, setSaveSuccess] = useState({});
  const fileInputRefs = useRef({});

  useEffect(() => {
    fetchPages();
  }, []);

  const fetchPages = async () => {
    try {
      const res = await axios.get(`${API}/settings/pages/seo`);
      setPages(res.data);
    } catch (err) {
      console.error("Error fetching page SEO:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (pageId) => {
    const page = pages.find(p => p.page_identifier === pageId);
    if (!page) return;

    setSaving(prev => ({ ...prev, [pageId]: true }));
    try {
      await axios.put(
        `${API}/settings/pages/seo/${pageId}`,
        {
          page_identifier: page.page_identifier,
          custom_slug: page.custom_slug || "",
          page_title: page.page_title || "",
          meta_description: page.meta_description || "",
          og_title: page.og_title || "",
          og_description: page.og_description || "",
          og_image: page.og_image || "",
          twitter_card: page.twitter_card || "summary_large_image",
          canonical_url: page.canonical_url || "",
          no_index: page.no_index || false
        },
        { headers: getAuthHeader() }
      );
      // Show success feedback
      setSaveSuccess(prev => ({ ...prev, [pageId]: true }));
      setTimeout(() => setSaveSuccess(prev => ({ ...prev, [pageId]: false })), 2000);
    } catch (err) {
      console.error("Error saving:", err);
      alert("Failed to save changes. Please try again.");
    } finally {
      setSaving(prev => ({ ...prev, [pageId]: false }));
    }
  };

  const updatePage = (pageId, field, value) => {
    setPages(prev => prev.map(p => 
      p.page_identifier === pageId ? { ...p, [field]: value } : p
    ));
  };

  const copyToClipboard = (text, id) => {
    navigator.clipboard.writeText(text);
    setCopied(id);
    setTimeout(() => setCopied(null), 2000);
  };

  const getFullUrl = (page) => {
    const baseUrl = window.location.origin;
    const path = page.custom_slug || page.default_path;
    return `${baseUrl}${path.startsWith('/') ? path : '/' + path}`;
  };

  // Handle file upload for OG image
  const handleFileUpload = async (pageId, file) => {
    if (!file) return;
    
    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];
    if (!allowedTypes.includes(file.type)) {
      alert('Please upload a JPG, PNG, WebP, or GIF image.');
      return;
    }
    
    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      alert('Image must be less than 5MB.');
      return;
    }
    
    setUploading(prev => ({ ...prev, [pageId]: true }));
    
    try {
      // Convert to base64
      const reader = new FileReader();
      reader.onload = async (e) => {
        const base64Data = e.target.result;
        
        // Upload to backend
        try {
          const res = await axios.post(
            `${API}/settings/pages/seo/${pageId}/upload-image`,
            { image_data: base64Data, filename: file.name },
            { headers: getAuthHeader() }
          );
          
          if (res.data.image_url) {
            updatePage(pageId, "og_image", res.data.image_url);
          }
        } catch (err) {
          console.error("Upload error:", err);
          // Fallback: use base64 directly
          updatePage(pageId, "og_image", base64Data);
        }
        
        setUploading(prev => ({ ...prev, [pageId]: false }));
      };
      reader.readAsDataURL(file);
    } catch (err) {
      console.error("File read error:", err);
      setUploading(prev => ({ ...prev, [pageId]: false }));
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-6 h-6 border-2 border-white/20 border-t-white rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-heading text-2xl font-bold uppercase tracking-tight text-white">
            Page SEO Settings
          </h2>
          <p className="text-zinc-500 text-sm mt-1">
            Customize URL slugs, page titles, and social sharing for each page
          </p>
        </div>
      </div>

      {/* Info Box */}
      <div className="p-4 bg-blue-500/10 border border-blue-500/20">
        <div className="flex items-start gap-3">
          <Search className="w-5 h-5 text-blue-400 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-blue-300">
            <p className="font-medium mb-1">SEO & Social Sharing Tips</p>
            <ul className="text-blue-300/80 space-y-1 text-xs">
              <li>• <strong>Page Title:</strong> Keep under 60 characters for optimal display in search results</li>
              <li>• <strong>Meta Description:</strong> 150-160 characters recommended</li>
              <li>• <strong>OG Image:</strong> Recommended size: 1200x630 pixels for best social sharing</li>
              <li>• <strong>Custom Slug:</strong> Use lowercase, hyphens for spaces (e.g., &quot;live-games&quot;)</li>
            </ul>
          </div>
        </div>
      </div>

      {/* Pages List */}
      <div className="space-y-4">
        {pages.map((page) => (
          <div 
            key={page.page_identifier}
            className="bg-zinc-900/50 border border-white/10 overflow-hidden"
          >
            {/* Page Header */}
            <button
              onClick={() => setExpandedPage(expandedPage === page.page_identifier ? null : page.page_identifier)}
              className="w-full flex items-center justify-between p-4 hover:bg-white/5 transition-colors"
            >
              <div className="flex items-center gap-4">
                <Globe className="w-5 h-5 text-zinc-400" />
                <div className="text-left">
                  <h3 className="font-heading text-lg font-bold text-white">{page.display_name}</h3>
                  <p className="text-xs text-zinc-500">
                    {page.custom_slug ? `/${page.custom_slug}` : page.default_path}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                {page.page_title && (
                  <span className="text-xs text-green-500 bg-green-500/10 px-2 py-1">SEO Set</span>
                )}
                {page.og_image && (
                  <span className="text-xs text-blue-500 bg-blue-500/10 px-2 py-1">OG Image</span>
                )}
                {expandedPage === page.page_identifier ? (
                  <ChevronUp className="w-5 h-5 text-zinc-400" />
                ) : (
                  <ChevronDown className="w-5 h-5 text-zinc-400" />
                )}
              </div>
            </button>

            {/* Expanded Content */}
            {expandedPage === page.page_identifier && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: "auto", opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                className="border-t border-white/10 p-6 space-y-6"
              >
                {/* URL Settings */}
                <div className="space-y-4">
                  <h4 className="text-xs font-bold uppercase tracking-wider text-zinc-400 flex items-center gap-2">
                    <Link2 className="w-4 h-4" /> URL Settings
                  </h4>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs text-zinc-500 mb-2">Custom URL Slug</label>
                      <div className="flex items-center gap-2">
                        <span className="text-zinc-600">/</span>
                        <input
                          type="text"
                          value={page.custom_slug || ""}
                          onChange={(e) => updatePage(page.page_identifier, "custom_slug", e.target.value.toLowerCase().replace(/\s+/g, '-'))}
                          placeholder={page.default_path.replace('/', '')}
                          className="flex-1 bg-black border border-white/10 text-white px-3 py-2 text-sm focus:outline-none focus:border-white/30"
                        />
                      </div>
                      <p className="text-xs text-zinc-600 mt-1">Leave empty to use default: {page.default_path}</p>
                    </div>
                    
                    <div>
                      <label className="block text-xs text-zinc-500 mb-2">Full URL Preview</label>
                      <div className="flex items-center gap-2 bg-black/50 border border-white/5 px-3 py-2">
                        <span className="text-zinc-400 text-sm truncate flex-1">{getFullUrl(page)}</span>
                        <button
                          onClick={() => copyToClipboard(getFullUrl(page), page.page_identifier)}
                          className="text-zinc-500 hover:text-white"
                        >
                          {copied === page.page_identifier ? <Check className="w-4 h-4 text-green-500" /> : <Copy className="w-4 h-4" />}
                        </button>
                        <a href={getFullUrl(page)} target="_blank" rel="noopener noreferrer" className="text-zinc-500 hover:text-white">
                          <ExternalLink className="w-4 h-4" />
                        </a>
                      </div>
                    </div>
                  </div>
                </div>

                {/* SEO Settings */}
                <div className="space-y-4">
                  <h4 className="text-xs font-bold uppercase tracking-wider text-zinc-400 flex items-center gap-2">
                    <FileText className="w-4 h-4" /> SEO Settings
                  </h4>
                  
                  <div>
                    <label className="block text-xs text-zinc-500 mb-2">Page Title (Browser Tab & Search Results)</label>
                    <input
                      type="text"
                      value={page.page_title || ""}
                      onChange={(e) => updatePage(page.page_identifier, "page_title", e.target.value)}
                      placeholder={`${page.display_name} | Antler Sports Network`}
                      maxLength={70}
                      className="w-full bg-black border border-white/10 text-white px-3 py-2 text-sm focus:outline-none focus:border-white/30"
                    />
                    <p className="text-xs text-zinc-600 mt-1">{(page.page_title || "").length}/60 characters</p>
                  </div>
                  
                  <div>
                    <label className="block text-xs text-zinc-500 mb-2">Meta Description</label>
                    <textarea
                      value={page.meta_description || ""}
                      onChange={(e) => updatePage(page.page_identifier, "meta_description", e.target.value)}
                      placeholder="Brief description for search engine results..."
                      maxLength={200}
                      rows={3}
                      className="w-full bg-black border border-white/10 text-white px-3 py-2 text-sm focus:outline-none focus:border-white/30 resize-none"
                    />
                    <p className="text-xs text-zinc-600 mt-1">{(page.meta_description || "").length}/160 characters recommended</p>
                  </div>
                  
                  <div className="flex items-center gap-3">
                    <button
                      onClick={() => updatePage(page.page_identifier, "no_index", !page.no_index)}
                      className={`flex items-center gap-2 px-3 py-2 text-xs font-bold uppercase ${
                        page.no_index 
                          ? 'bg-red-500/20 text-red-500 border border-red-500/30' 
                          : 'bg-zinc-800 text-zinc-400 border border-white/10'
                      }`}
                    >
                      {page.no_index ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      {page.no_index ? "Hidden from Search" : "Indexed by Search"}
                    </button>
                  </div>
                </div>

                {/* Social Sharing Settings */}
                <div className="space-y-4">
                  <h4 className="text-xs font-bold uppercase tracking-wider text-zinc-400 flex items-center gap-2">
                    <Image className="w-4 h-4" /> Social Sharing (Open Graph)
                  </h4>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs text-zinc-500 mb-2">OG Title (Social Share Title)</label>
                      <input
                        type="text"
                        value={page.og_title || ""}
                        onChange={(e) => updatePage(page.page_identifier, "og_title", e.target.value)}
                        placeholder="Title shown when shared on social media"
                        className="w-full bg-black border border-white/10 text-white px-3 py-2 text-sm focus:outline-none focus:border-white/30"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-xs text-zinc-500 mb-2">OG Description</label>
                      <input
                        type="text"
                        value={page.og_description || ""}
                        onChange={(e) => updatePage(page.page_identifier, "og_description", e.target.value)}
                        placeholder="Description shown when shared"
                        className="w-full bg-black border border-white/10 text-white px-3 py-2 text-sm focus:outline-none focus:border-white/30"
                      />
                    </div>
                  </div>
                  
                  {/* OG Image with URL or Upload */}
                  <div>
                    <label className="block text-xs text-zinc-500 mb-2">OG Image (Social Share Image)</label>
                    
                    {/* Toggle between URL and Upload */}
                    <div className="flex items-center gap-2 mb-3">
                      <button
                        onClick={() => setImageInputMode(prev => ({ ...prev, [page.page_identifier]: 'url' }))}
                        className={`px-3 py-1.5 text-xs font-bold uppercase ${
                          (imageInputMode[page.page_identifier] || 'url') === 'url'
                            ? 'bg-orange-500 text-black'
                            : 'bg-zinc-800 text-zinc-400 hover:text-white'
                        }`}
                      >
                        <Link2 className="w-3 h-3 inline mr-1" />
                        URL Link
                      </button>
                      <button
                        onClick={() => setImageInputMode(prev => ({ ...prev, [page.page_identifier]: 'upload' }))}
                        className={`px-3 py-1.5 text-xs font-bold uppercase ${
                          imageInputMode[page.page_identifier] === 'upload'
                            ? 'bg-orange-500 text-black'
                            : 'bg-zinc-800 text-zinc-400 hover:text-white'
                        }`}
                      >
                        <Upload className="w-3 h-3 inline mr-1" />
                        Upload File
                      </button>
                    </div>
                    
                    {/* URL Input */}
                    {(imageInputMode[page.page_identifier] || 'url') === 'url' && (
                      <input
                        type="url"
                        value={page.og_image || ""}
                        onChange={(e) => updatePage(page.page_identifier, "og_image", e.target.value)}
                        placeholder="https://example.com/share-image.jpg"
                        className="w-full bg-black border border-white/10 text-white px-3 py-2 text-sm focus:outline-none focus:border-white/30"
                      />
                    )}
                    
                    {/* File Upload */}
                    {imageInputMode[page.page_identifier] === 'upload' && (
                      <div className="space-y-2">
                        <input
                          type="file"
                          ref={el => fileInputRefs.current[page.page_identifier] = el}
                          accept="image/jpeg,image/png,image/webp,image/gif"
                          onChange={(e) => handleFileUpload(page.page_identifier, e.target.files[0])}
                          className="hidden"
                        />
                        <button
                          onClick={() => fileInputRefs.current[page.page_identifier]?.click()}
                          disabled={uploading[page.page_identifier]}
                          className="w-full bg-zinc-800 border border-dashed border-zinc-600 text-zinc-400 hover:text-white hover:border-zinc-500 px-4 py-6 text-sm flex flex-col items-center justify-center gap-2 transition-colors"
                        >
                          {uploading[page.page_identifier] ? (
                            <>
                              <Loader2 className="w-6 h-6 animate-spin" />
                              <span>Uploading...</span>
                            </>
                          ) : (
                            <>
                              <Upload className="w-6 h-6" />
                              <span>Click to upload image</span>
                              <span className="text-xs text-zinc-600">JPG, PNG, WebP, GIF (max 5MB)</span>
                            </>
                          )}
                        </button>
                      </div>
                    )}
                    
                    <p className="text-xs text-zinc-600 mt-1">Recommended: 1200x630 pixels, JPG or PNG</p>
                  </div>
                  
                  {/* OG Image Preview */}
                  {page.og_image && (
                    <div className="p-4 bg-black/50 border border-white/5">
                      <div className="flex items-center justify-between mb-2">
                        <p className="text-xs text-zinc-500">Image Preview:</p>
                        <button
                          onClick={() => updatePage(page.page_identifier, "og_image", "")}
                          className="text-xs text-red-500 hover:text-red-400"
                        >
                          Remove Image
                        </button>
                      </div>
                      <img 
                        src={page.og_image} 
                        alt="OG Preview" 
                        className="max-w-xs h-auto border border-white/10"
                        onError={(e) => e.target.style.display = 'none'}
                      />
                    </div>
                  )}
                </div>

                {/* Save Button */}
                <div className="flex justify-end pt-4 border-t border-white/10">
                  <Button
                    onClick={() => handleSave(page.page_identifier)}
                    disabled={saving[page.page_identifier]}
                    className={`text-xs px-6 py-2 h-auto rounded-none ${
                      saveSuccess[page.page_identifier]
                        ? 'bg-green-500 text-white hover:bg-green-500'
                        : 'bg-white text-black hover:bg-zinc-200'
                    }`}
                    data-testid={`save-seo-${page.page_identifier}`}
                  >
                    {saveSuccess[page.page_identifier] ? (
                      <>
                        <Check className="w-4 h-4 mr-2" />
                        Saved!
                      </>
                    ) : saving[page.page_identifier] ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Saving...
                      </>
                    ) : (
                      <>
                        <Save className="w-4 h-4 mr-2" />
                        Save Changes
                      </>
                    )}
                  </Button>
                </div>
              </motion.div>
            )}
          </div>
        ))}
      </div>
    </motion.div>
  );
};

export default PageSEOTab;
